import {
  EXTEND_FIELDS,
  REQUIRED_FIELD_KEYS,
} from 'src/app/BulkInvite/bulkInvite.constants'
import {
  ColumnMapping,
  ExtendFieldKey,
} from 'src/app/BulkInvite/bulkInvite.types'

const EMPTY_MAPPING: ColumnMapping = {
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  role: null,
}

// Minimum confidence required to auto-map a column. Below this we leave the
// column UNMAPPED ("Do not import") rather than guessing — the admin confirms on
// the Mapping step, so a wrong guess is worse than no guess. Tuned so an exact /
// compact / whole-phrase alias match passes but a lone weak token buried in a
// noisy header (e.g. "Registered Card Last 4" → lastName via "last") does not.
const MIN_CONFIDENCE = 75

/** Normalize a header so "Work Email_Address" and "work email address" match. */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function tokenize(s: string): string[] {
  return normalize(s).split(' ').filter(Boolean)
}

// Generic, semantically-empty qualifiers that commonly lead/trail an HRIS column
// name without changing which field it is — "Employee First Name", "Personal
// Email", "Manager Phone Number" are all still first name / email / phone. We
// strip these (only at the head/tail, never the interior) before scoring so a
// lone generic prefix no longer drags a true match below MIN_CONFIDENCE. This is
// deliberately a small, hand-picked set: it must NOT include meaning-bearing
// words like "extension" ("Phone Extension" is not a phone) so those still fall
// through to the low-coverage tier and stay unmapped.
const GENERIC_QUALIFIERS = new Set([
  'employee',
  'personal',
  'primary',
  'home',
  'work',
  'manager',
])

/**
 * Drop leading/trailing generic qualifier tokens. Only the edges are trimmed —
 * an interior qualifier is left alone — and at least one token always remains.
 */
function stripQualifiers(tokens: string[]): string[] {
  let start = 0
  let end = tokens.length
  while (end - start > 1 && GENERIC_QUALIFIERS.has(tokens[start])) start++
  while (end - start > 1 && GENERIC_QUALIFIERS.has(tokens[end - 1])) end--
  return tokens.slice(start, end)
}

/**
 * True when `needle`'s tokens appear in `haystack` as a contiguous, in-order run
 * — a word-boundary phrase match. This is the precise replacement for the old
 * raw `includes()` substring rule: "last name" matches "legal last name" but a
 * bare "last" no longer matches "registered card last 4" as a phrase win.
 */
function containsPhrase(haystack: string[], needle: string[]): boolean {
  if (needle.length === 0 || needle.length > haystack.length) return false
  for (let i = 0; i <= haystack.length - needle.length; i++) {
    let matched = true
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) {
        matched = false
        break
      }
    }
    if (matched) return true
  }
  return false
}

/**
 * Score a single alias against one pre-tokenized view of a header.
 * `hTokens` is the token list, `hCompact` its whitespace-free join.
 */
function scoreAliasAgainstTokens(
  aTokens: string[],
  aCompact: string,
  hTokens: string[],
  hCompact: string,
): number {
  if (aTokens.length === 0) return 0
  // Exact normalized match (token lists identical, in order).
  if (
    hTokens.length === aTokens.length &&
    hTokens.every((t, i) => t === aTokens[i])
  ) {
    return 100
  }
  if (hCompact === aCompact) return 95
  if (containsPhrase(hTokens, aTokens)) {
    // The whole alias phrase is present at a word boundary. Score it at a near-
    // constant high tier with a flat penalty per *extra* header token rather
    // than a linear coverage ratio. The penalty is steep enough that even one
    // extra *meaning-bearing* token drops below MIN_CONFIDENCE ("Phone
    // Extension" → 70, unmapped — an extension is not a phone). Generic
    // qualifiers ("Employee Phone") don't rely on this tier at all: they are
    // recovered by the qualifier-stripped pass in scoreMatch, which yields an
    // exact match. A noisy header ("registered card last 4") drops far below.
    const extraTokens = hTokens.length - aTokens.length
    return Math.max(0, 90 - extraTokens * 20)
  }
  // No phrase match — only count shared tokens, and only credit multi-token
  // overlap. A single shared weak token ("last", "cell") stays below
  // MIN_CONFIDENCE so it is left unmapped rather than guessed.
  const aTokenSet = new Set(aTokens)
  const overlap = hTokens.filter((t) => aTokenSet.has(t)).length
  return overlap >= 2 ? 50 + overlap * 5 : 0
}

/**
 * Score how well a CSV header matches a field's aliases (0 = no match).
 *
 * Scoring is token/word-boundary based, not substring based, so noisy headers
 * that merely contain an alias's characters ("Registered Card Last 4") no longer
 * win on a false positive. Each alias is scored against two views of the header:
 * the raw header and a copy with generic leading/trailing qualifiers stripped
 * ("Employee First Name" → "First Name"); the better of the two wins, so adding
 * a qualifier set can only recover true matches, never regress an existing one.
 * Tiers, highest first:
 *   100 — exact normalized match
 *    95 — compact match (whitespace removed): "firstname" === "first name"
 *  ≤ 90 — the whole alias phrase appears contiguously at a word boundary, minus
 *         a flat penalty per extra header token (one generic qualifier stays
 *         high; several noisy tokens fall below MIN_CONFIDENCE)
 *  ≤ 60 — only loose token overlap; below MIN_CONFIDENCE, so left unmapped
 */
function scoreMatch(aliases: string[], header: string): number {
  const hTokens = tokenize(header)
  if (hTokens.length === 0) return 0
  const hCompact = hTokens.join('')
  const hStripped = stripQualifiers(hTokens)
  const hStrippedCompact = hStripped.join('')

  let best = 0
  for (const alias of aliases) {
    const aTokens = tokenize(alias)
    if (aTokens.length === 0) continue
    const aCompact = aTokens.join('')

    best = Math.max(
      best,
      scoreAliasAgainstTokens(aTokens, aCompact, hTokens, hCompact),
    )
    // Re-score against the qualifier-stripped header so "Employee First Name"
    // matches "first name". Skip when stripping changed nothing.
    if (hStripped.length !== hTokens.length) {
      best = Math.max(
        best,
        scoreAliasAgainstTokens(aTokens, aCompact, hStripped, hStrippedCompact),
      )
    }
    if (best === 100) break
  }
  return best
}

/**
 * Greedily auto-map CSV headers to Extend fields by best fuzzy score.
 * Each header and each field is used at most once.
 */
export function autoMapColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = { ...EMPTY_MAPPING }

  type Candidate = { field: ExtendFieldKey; header: string; score: number }
  const candidates: Candidate[] = []
  for (const field of EXTEND_FIELDS) {
    for (const header of headers) {
      const score = scoreMatch(field.aliases, header)
      // Below the confidence floor we leave the column unmapped ("Do not
      // import") instead of guessing — a wrong auto-map is worse than none.
      if (score >= MIN_CONFIDENCE) {
        candidates.push({ field: field.key, header, score })
      }
    }
  }
  candidates.sort((a, b) => b.score - a.score)

  const usedHeaders = new Set<string>()
  const usedFields = new Set<ExtendFieldKey>()
  for (const c of candidates) {
    if (usedFields.has(c.field) || usedHeaders.has(c.header)) continue
    mapping[c.field] = c.header
    usedFields.add(c.field)
    usedHeaders.add(c.header)
  }
  return mapping
}

/** Field keys still missing a mapping that are required to proceed. */
export function missingRequiredFields(
  mapping: ColumnMapping,
): ExtendFieldKey[] {
  return REQUIRED_FIELD_KEYS.filter((key) => mapping[key] === null)
}

export function isMappingComplete(mapping: ColumnMapping): boolean {
  return missingRequiredFields(mapping).length === 0
}
